// CandidateService.js - Service for managing candidates in role positions

import Directory from "components/dashboards/analytics/directory/Directory";
import DirectoryService from "../directory/DirectoryService";
import axios from "axios";
  const isUUID = value => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  };
const CandidateService = {
  getCandidatesByRoleAndStatus: async (roleIdentifier, status) => {
    try {
      const url = `/api/roleposition/job-application/${roleIdentifier}/status/${status}`;
      const response = await fetch(url);
      const data = await response.json();
      return data.map(item => ({
        id: item.identifier,
        text: `${item.firstName} ${item.lastName}`
      }));
    } catch (error) {
      console.error(
        `Error fetching candidates by stage for role ${roleIdentifier}:`,
        error
      );
      throw error;
    }
  },
  /**
   * Get candidates grouped by recruitment stage for a specific role position
   * @param {string} roleIdentifier - The identifier of the role position
   * @returns {Promise<Array>} - Promise resolving to array of stage objects with candidates
   */
  getCandidatesByStage: async roleIdentifier => {
    // Check if demo mode is enabled
        // Production mode - fetch from API
    const submittingApplicationCandidates = await CandidateService.getCandidatesByRoleAndStatus(
      roleIdentifier,
      'submitting-application');
    const applicationReviewCandidates = await CandidateService.getCandidatesByRoleAndStatus(
      roleIdentifier,
      'application-review');
    const screeningCandidates = await CandidateService.getCandidatesByRoleAndStatus(
      roleIdentifier,
      'screening');
    const interviewCandidates = await CandidateService.getCandidatesByRoleAndStatus(
      roleIdentifier,
      'interview');

    const bstDemoMode = localStorage.getItem('bstDemoMode');
    if (bstDemoMode === 'true') {
      // Return mock data for demo mode
      return new Promise(resolve => {
        setTimeout(() => {
          const mockData = [
            {
              id: 'submitting-application',
              title: 'Submitting Application',
              contents: [
                {
                  id: 'candidate-001',
                  text: 'John Smith'
                },
                {
                  id: 'candidate-002',
                  text: 'Sarah Johnson'
                },
                {
                  id: 'candidate-003',
                  text: 'Michael Chen'
                },
                {
                  id: 'candidate-004',
                  text: 'Emma Davis'
                },
                ...submittingApplicationCandidates
              ]
            },
            {
              id: 'application-review',
              title: 'Application Review',
              contents: [
                {
                  id: 'candidate-005',
                  text: 'Robert Williams'
                },
                {
                  id: 'candidate-006',
                  text: 'Lisa Anderson'
                },
                {
                  id: 'candidate-007',
                  text: 'David Martinez'
                },
                ...applicationReviewCandidates
              ]
            },
            {
              id: 'screening',
              title: 'Screening',
              contents: [
                {
                  id: 'candidate-008',
                  text: 'Jennifer Taylor'
                },
                {
                  id: 'candidate-009',
                  text: 'James Brown'
                },
                ...screeningCandidates

              ]
            },
            {
              id: 'interview',
              title: 'Interview',
              contents: [
                {
                  id: 'candidate-010',
                  text: 'Patricia Garcia'
                },
                ...interviewCandidates
              ]
            }
          ];
          resolve(mockData);
        }, 300); // Simulate network delay
      });
    }



    return [
      {
        id: 'submitting-application',
        title: 'Submitting Application',
        contents: submittingApplicationCandidates
      },
      {
        id: 'application-review',
        title: 'Application Review',
        contents: applicationReviewCandidates
      },
      {
        id: 'screening',
        title: 'Screening',
        contents: screeningCandidates
      },
      {
        id: 'interview',
        title: 'Interview',
        contents: interviewCandidates
      }
    ];
  },

  /**
   * Update candidate stage
   * @param {string} candidateId - The candidate identifier
   * @param {string} newStage - The new stage identifier
   * @returns {Promise<Object>} - Promise resolving to success response
   */
  updateCandidateStage: async (roleIdentifier, candidateId, newStage) => {
    if(!isUUID(candidateId)){ 
      console.log('Invalid candidate identifier');
      return;
    }  
    try {
      const results = [];
      const updatedJobApplication = {
        id: {
          jobVaccancyIdentifier: roleIdentifier,
          candidateIdentifier: candidateId
        },
        status: newStage
      };
      const url = `/api/roleposition/job-application`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedJobApplication)
      });

      if (response.ok) {
        const data = await response.json();
        results.push(data);
      } else {
        console.error(
          `Failed to add candidate ${candidateId} to role ${roleIdentifier}`
        );
      }

      return {
        success: true,
        message: `${results.length} candidate(s) added to role ${roleIdentifier}`,
        addedCandidates: results
      };
    } catch (error) {
      console.error('Error adding candidates to role:', error);
      throw error;
    }
  },
  retrieveAllCandidateByAccount: async () => {
    try {
      const { accountIdentifier } = JSON.parse(localStorage.getItem('profile'));
      const url = '/api/candidate/account/' + accountIdentifier;
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Candidate', error);
      throw error;
    }
  },
  /**
   * Get all available candidates (users that can be added to a role position)
   * @returns {Promise<Array>} - Promise resolving to array of candidates
   */
  getAvailableCandidates: async () => {
    try {
      const { accountIdentifier } = JSON.parse(localStorage.getItem('profile'));
      const response = await fetch(
        '/api/candidate/account/' + accountIdentifier
      );
      const data = await response.json();

      // Convert backend data to select format
      let candidates = data.map(candidate => ({
        label: `${candidate.firstName} ${candidate.lastName}`,
        value: candidate.identifier
      }));

      // Check if demo mode is enabled
      const bstDemoMode = localStorage.getItem('bstDemoMode');
      if (bstDemoMode === 'true') {
        // Mock data for demo mode
        const mockCandidates = [
          { label: 'Alice Thompson', value: 'candidate-011' },
          { label: 'Brian Wilson', value: 'candidate-012' },
          { label: 'Catherine Lee', value: 'candidate-013' },
          { label: 'Daniel Moore', value: 'candidate-014' },
          { label: 'Emily Harris', value: 'candidate-015' },
          { label: 'Frank Miller', value: 'candidate-016' },
          { label: 'Grace Turner', value: 'candidate-017' },
          { label: 'Henry Scott', value: 'candidate-018' },
          { label: 'Isabella White', value: 'candidate-019' },
          { label: 'Jack Robinson', value: 'candidate-020' },
          { label: 'Karen Martinez', value: 'candidate-021' },
          { label: 'Luis Garcia', value: 'candidate-022' },
          { label: 'Monica Davis', value: 'candidate-023' },
          { label: 'Nathan Clark', value: 'candidate-024' },
          { label: 'Olivia Lewis', value: 'candidate-025' }
        ];
        // Merge backend data with mock data
        candidates = [...candidates, ...mockCandidates];
      }

      return candidates;
    } catch (error) {
      console.error('Error fetching available candidates:', error);
      throw error;
    }
  },

  /**
   * Add candidates to a role position
   * @param {string} roleIdentifier - The role position identifier
   * @param {Array} candidateIds - Array of candidate identifiers to add
   * @returns {Promise<Object>} - Promise resolving to success response
   */
  addCandidatesToRole: async (roleIdentifier, candidateIds) => {
    try {
      const results = [];

      // Post to API for each candidate
      for (const candidateId of candidateIds) {
        const url = `/api/roleposition/role/${roleIdentifier}/candidate/${candidateId}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          results.push(data);
        } else {
          console.error(
            `Failed to add candidate ${candidateId} to role ${roleIdentifier}`
          );
        }
      }

      return {
        success: true,
        message: `${results.length} candidate(s) added to role ${roleIdentifier}`,
        addedCandidates: results
      };
    } catch (error) {
      console.error('Error adding candidates to role:', error);
      throw error;
    }
  },

  /**
   * Remove a candidate from a role position
   * @param {string} roleIdentifier - The role position identifier
   * @param {string} candidateId - The candidate identifier
   * @returns {Promise<Object>} - Promise resolving to success response
   */
  removeCandidate: async (roleIdentifier, candidateId) => {
    try {
      const url = `/api/roleposition/role/${roleIdentifier}/candidate/${candidateId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Candidate removed from role:', data);
        return {
          success: true,
          message: `Candidate ${candidateId} removed from role ${roleIdentifier}`,
          data: data
        };
      } else {
        throw new Error(
          `Failed to remove candidate ${candidateId} from role ${roleIdentifier}`
        );
      }
    } catch (error) {
      console.error('Error removing candidate from role:', error);
      throw error;
    }
  },

  /**
   * Save a candidate (create new or update existing)
   * @param {Object} formData - The candidate form data
   * @returns {Promise<Object>} - Promise resolving to saved candidate
   */
  async saveCandidate(formData) {
    try {
      let url = '/api/candidate';
      let method = 'POST';
      const profileLogged = JSON.parse(localStorage.getItem('profile'));
      const { accountIdentifier } = profileLogged;

      if (formData.identifier !== undefined) {
        url = '/api/candidate/' + formData.identifier;
        method = 'PUT';
      }

      const updatedData = {
        ...formData,
        profileIdentifier: profileLogged.identifier,
        accountIdentifier: accountIdentifier
      };

      console.log('Saving candidate:', updatedData);
      if (
        updatedData.cv === null ||
          updatedData.cv === undefined ||
        typeof updatedData.cv === 'string'
      ) {
        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatedData)
        });

        if (response.ok) {
          const savedCandidate = await response.json();
          console.log('Candidate has been saved:', savedCandidate);
          return savedCandidate;
        } else {
          throw new Error('Failed to save candidate');
        }
      } else {
        return this.saveCandidateWithAttachment(updatedData);
      }
    } catch (error) {
      console.error('Error saving candidate:', error);
      throw error;
    }
  },
  async saveCandidateWithAttachment(candidate) {
    const { identifier } = DirectoryService.retrieveLoggedProfile();
    const candidateId = candidate?.identifier;
    const baseUrl = '/api';
    const url = candidateId
      ? `${baseUrl}/candidate/${candidateId}/profile/${identifier}/with-attachment`
      : `${baseUrl}/candidate/profile/${identifier}/with-attachment`;
    const file = candidate.cv;
    delete candidate.profileIdentifier;
    const updatedCandidate = {
      ...candidate, 
      cv: candidate.cv.name
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);
    formData.append('payload', JSON.stringify(updatedCandidate));
    const config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    };

    if (candidate.identifier !== undefined) {
      axios.put(url, formData, config).then(response => {
        console.log(response.data);
      });
    } else {
      axios.post(url, formData, config).then(response => {
        console.log(response.data);
      });
    }
  },
  /**
   * Retrieve a candidate by identifier
   * @param {string} identifier - The candidate identifier
   * @returns {Promise<Object>} - Promise resolving to candidate data
   */
  async retrieveCandidate(identifier) {
    try {
      const response = await fetch('/api/candidate/' + identifier);
      const data = await response.json();
      console.log('Retrieved candidate:', data);
      return data;
    } catch (error) {
      console.error('Error fetching candidate:', error);
      throw error;
    }
  }
};

export default CandidateService;
